import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronRight, MessageCircle } from "lucide-react";
import { WA_LINK } from "./shared";
import { WEDDING_CITIES } from "@/weddingsConfig";
import { CITIES } from "@/cityConfig";

const T = {
  ru: { shop: "МАГАЗИН", all_weddings: "ВСЕ СВАДЬБЫ", cities: "ГОРОДА", blog: "БЛОГ", cta: "ЗАПРОСИТЬ СМЕТУ", menu: "МЕНЮ" },
  en: { shop: "SHOP", all_weddings: "ALL WEDDINGS", cities: "CITIES", blog: "BLOG", cta: "GET A QUOTE", menu: "MENU" },
  tr: { shop: "MAĞAZA", all_weddings: "TÜM DÜĞÜNLER", cities: "ŞEHİRLER", blog: "BLOG", cta: "TEKLİF AL", menu: "MENÜ" },
};

// Mobile (and tablet) hamburger menu for the wedding hub & city pages.
// Dark/gold aesthetic to match the weddings nav over the hero.
export default function WeddingMobileMenu({ lang, citySlug }) {
  const [open, setOpen] = useState(false);
  const t = T[lang] || T.ru;
  const q = lang !== "ru" ? `?lang=${lang}` : "";
  const shopHref = `${CITIES[citySlug] ? `/${citySlug}` : "/"}${q}`;

  const cityLabel = (cfg) => (lang === "ru" ? cfg.name : cfg.nameEn);

  const linkCls = "flex items-center justify-between py-3.5 text-[13px] tracking-[0.18em] text-white/85 hover:text-[#F5C77A] transition-colors border-b border-white/10";

  return (
    <div className="lg:hidden">
      <button
        onClick={() => setOpen(true)}
        aria-label={t.menu}
        className="p-2 -mr-1 text-white/90 hover:text-[#F5C77A] transition-colors"
        data-testid="weddings-mobile-menu-btn"
      >
        <Menu className="w-6 h-6" strokeWidth={1.5} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50" data-testid="weddings-mobile-menu">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="weddings-mobile-menu-backdrop"
          />
          {/* Panel */}
          <div className="absolute top-0 right-0 h-full w-[82%] max-w-[360px] bg-[#0A0807] shadow-2xl flex flex-col animate-fade-in overflow-y-auto overscroll-contain">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <span className="text-[11px] tracking-[0.3em] text-[#F5C77A] font-light">{t.menu}</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="p-1 text-white/80 hover:text-white transition-colors"
                data-testid="weddings-mobile-menu-close"
              >
                <X className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>

            <nav className="px-6 pb-6 flex-1">
              <Link to={shopHref} onClick={() => setOpen(false)} className={linkCls} data-testid="weddings-mobile-shop">
                <span>{t.shop}</span>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </Link>
              <Link to={`/weddings${q}`} onClick={() => setOpen(false)} className={linkCls} data-testid="weddings-mobile-all">
                <span>{t.all_weddings}</span>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </Link>

              <p className="pt-5 pb-1 text-[10px] tracking-[0.3em] text-[#F5C77A]/70 font-light">{t.cities}</p>
              {Object.values(WEDDING_CITIES).map((cfg) => (
                <Link
                  key={cfg.slug}
                  to={`/weddings/${cfg.slug}${q}`}
                  onClick={() => setOpen(false)}
                  className={`${linkCls} ${cfg.slug === citySlug ? "text-[#F5C77A]" : ""}`}
                  data-testid={`weddings-mobile-city-${cfg.slug}`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: "1.1rem", letterSpacing: "0.04em" }}
                >
                  <span>{cityLabel(cfg)}</span>
                  <ChevronRight className="w-4 h-4 opacity-50" />
                </Link>
              ))}

              <Link to={`/blog${q}`} onClick={() => setOpen(false)} className={`${linkCls} mt-5`} data-testid="weddings-mobile-blog">
                <span>{t.blog}</span>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </Link>

              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-[#FFE5A8] via-[#F5C77A] to-[#E0A954] text-[#0A0807] text-[11px] tracking-[0.25em] font-semibold rounded-sm"
                data-testid="weddings-mobile-cta"
              >
                <MessageCircle className="w-4 h-4" />
                <span>{t.cta}</span>
              </a>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
